import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Briefcase, Calendar, Clock, DollarSign, Star, Users, LogOut, Plus, MapPin, Eye, CheckCircle, XCircle, Search, FileText, Sparkles, Send, Loader2, Award, ChevronDown, ChevronUp, Megaphone, Trash2, MessageSquare, Video, UserPlus, Shield, Edit2, Bookmark } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import TrainingContract from '../components/TrainingContract';
import NotificationBell from '../components/NotificationBell';
import ProfileImageUpload from '../components/ProfileImage';

export default function CorporateDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, logout } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [myAds, setMyAds] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCorpTab, setActiveCorpTab] = useState('jobs');
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const [showCreateAd, setShowCreateAd] = useState(false);
  const [showInviteTeam, setShowInviteTeam] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: '', email: '', role: 'viewer' });
  const [adForm, setAdForm] = useState({ headline: '', description: '', link_url: '', training_types: '' });
  
  const [showScheduleInterview, setShowScheduleInterview] = useState(false);
  const [interviewForm, setInterviewForm] = useState({
    trainer_id: '', job_id: '', proposed_datetime: '', duration_minutes: 30, meet_link: '', notes: ''
  });
  
  const [bookingDialog, setBookingDialog] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [contractBooking, setContractBooking] = useState(null);
  const [bookingFilter, setBookingFilter] = useState('all');
  const [editingCorpProfile, setEditingCorpProfile] = useState(false);
  const [corpProfileForm, setCorpProfileForm] = useState({
    company_name: '', industry: '', location: '', company_size: '', website: '', contact_phone: '', about: ''
  });
  const [bookingForm, setBookingForm] = useState({
    start_datetime: '',
    end_datetime: '',
    amount: 0
  });

  const fetchData = useCallback(async () => {
    if (!user || !token) return;
    
    try {
      // Fetch profile
      const profileRes = await axios.get(`${API}/corporates/user/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(profileRes.data);
      setCorpProfileForm({
        company_name: profileRes.data.company_name || '',
        industry: profileRes.data.industry || '',
        location: profileRes.data.location || '',
        company_size: profileRes.data.company_size || '',
        website: profileRes.data.website || '',
        contact_phone: profileRes.data.contact_phone || '',
        about: profileRes.data.about || ''
      });

      // Fetch stats
      const statsRes = await axios.get(`${API}/stats/corporate/${profileRes.data.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);

      // Fetch jobs
      const jobsRes = await axios.get(`${API}/jobs/corporate/${profileRes.data.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(jobsRes.data);

      // Fetch bookings
      const bookingsRes = await axios.get(`${API}/bookings/corporate/${profileRes.data.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(bookingsRes.data);

      // Fetch my ads
      try {
        const adsRes = await axios.get(`${API}/ads/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMyAds(adsRes.data);
      } catch (e) { /* no ads yet */ }

      // Fetch interviews
      try {
        const interviewsRes = await axios.get(`${API}/interviews/corporate/${profileRes.data.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInterviews(interviewsRes.data);
      } catch (e) { /* no interviews yet */ }

      // Fetch team members
      try {
        const teamRes = await axios.get(`${API}/teams/${profileRes.data.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTeamMembers(teamRes.data);
      } catch (e) { /* no team yet */ }

      // Fetch unread message count
      try {
        const unreadRes = await axios.get(`${API}/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadMsgCount(unreadRes.data.unread_count);
      } catch (e) { /* no messages yet */ }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const bookTrainerId = searchParams.get('book');
    if (bookTrainerId) {
      fetchTrainerForBooking(bookTrainerId);
    }
    const tab = searchParams.get('tab');
    if (tab) setActiveCorpTab(tab);
  }, [searchParams]);

  const fetchTrainerForBooking = async (trainerId) => {
    try {
      const response = await axios.get(`${API}/trainers/${trainerId}`);
      setSelectedTrainer(response.data);
      setBookingDialog(true);
    } catch (error) {
      toast.error('Failed to load trainer details');
    }
  };

  const handleCreateBooking = async () => {
    if (!selectedTrainer || !selectedJob) {
      toast.error('Please select a job for this booking');
      return;
    }
    if (!bookingForm.start_datetime || !bookingForm.end_datetime) {
      toast.error('Please select date and time');
      return;
    }

    try {
      await axios.post(`${API}/bookings`, {
        job_id: selectedJob.id,
        trainer_id: selectedTrainer.id,
        start_datetime: bookingForm.start_datetime,
        end_datetime: bookingForm.end_datetime,
        amount: bookingForm.amount || selectedTrainer.hourly_rate * 8
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Trainer booked successfully!');
      setBookingDialog(false);
      setSelectedTrainer(null);
      setSelectedJob(null);
      setBookingForm({ start_datetime: '', end_datetime: '', amount: 0 });
      fetchData();
      navigate('/corporate/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    }
  };

  const handleUpdateApplicationStatus = async (appId, status) => {
    try {
      await axios.put(`${API}/applications/${appId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Application ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update application status');
    }
  };

  const handleScheduleInterview = async () => {
    if (!interviewForm.trainer_id || !interviewForm.job_id || !interviewForm.proposed_datetime) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await axios.post(`${API}/interviews`, interviewForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Interview scheduled! Trainer will be notified.');
      setShowScheduleInterview(false);
      setInterviewForm({ trainer_id: '', job_id: '', proposed_datetime: '', duration_minutes: 30, meet_link: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule interview');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="corporate-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-bold text-xl text-slate-900">Training Solutions</span>
            </Link>

            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/search?type=trainers')}
                className="text-slate-600"
                data-testid="find-trainers-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Find Trainers
              </Button>
              <Button
                onClick={() => navigate('/corporate/post-job')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="post-job-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Post Requirement
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/corporate/shortlist')}
                className="text-slate-600"
                data-testid="shortlist-link"
              >
                <Bookmark className="w-4 h-4 mr-2" />
                Shortlist
              </Button>
              <NotificationBell />
              <Button
                variant="ghost"
                onClick={() => navigate('/messages')}
                className="text-slate-600 relative"
                data-testid="messages-link"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Messages
                {unreadMsgCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center" data-testid="unread-badge">{unreadMsgCount}</span>
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={logout}
                className="text-slate-600"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8 flex items-center gap-5">
          <ProfileImageUpload
            currentImage={profile?.logo}
            name={profile?.company_name}
            size="lg"
            onUploadSuccess={(path) => setProfile(prev => ({ ...prev, logo: path }))}
          />
          <div>
            <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-1">
              Welcome, {profile?.company_name}!
            </h1>
            <p className="text-slate-500">Manage your training requirements, bookings, and advertisements</p>
            <p className="text-xs text-slate-400 mt-1">Click your logo to upload a company image</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all" onClick={() => navigate('/corporate/jobs')} data-testid="stat-jobs-posted">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-slate-500">Jobs Posted</span>
            </div>
            <div className="font-outfit text-3xl font-bold text-slate-900">
              {stats?.total_jobs_posted || 0}
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:border-emerald-300 hover:shadow-sm transition-all" onClick={() => navigate('/corporate/jobs?status=open')} data-testid="stat-open-jobs">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-sm text-slate-500">Open Requirements</span>
            </div>
            <div className="font-outfit text-3xl font-bold text-slate-900">
              {stats?.open_jobs || 0}
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:border-purple-300 hover:shadow-sm transition-all" onClick={() => navigate('/corporate/bookings')} data-testid="stat-bookings">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-slate-500">Total Bookings</span>
            </div>
            <div className="font-outfit text-3xl font-bold text-slate-900">
              {stats?.total_bookings || 0}
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:border-amber-300 hover:shadow-sm transition-all" onClick={() => navigate('/corporate/spending')} data-testid="stat-total-spent">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-sm text-slate-500">Total Spent</span>
            </div>
            <div className="font-outfit text-3xl font-bold text-slate-900">
              ₹{(stats?.total_spent || 0).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <Tabs value={activeCorpTab} onValueChange={setActiveCorpTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-lg">
            <TabsTrigger value="jobs" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-jobs">
              Training Requirements ({jobs.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-bookings">
              Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="ads" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-ads">
              <Megaphone className="w-4 h-4 mr-1" />
              My Ads ({myAds.length})
            </TabsTrigger>
            <TabsTrigger value="interviews" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-interviews">
              <Video className="w-4 h-4 mr-1" />
              Interviews ({interviews.length})
            </TabsTrigger>
            <TabsTrigger value="team" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-team">
              <Users className="w-4 h-4 mr-1" />
              Team ({teamMembers.length})
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-corp-profile">
              <Edit2 className="w-4 h-4 mr-1" />
              Profile
            </TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <div className="space-y-4">
              {jobs.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No requirements posted yet</h3>
                  <p className="text-slate-500 mb-6">Create your first training requirement to find expert trainers</p>
                  <Button onClick={() => navigate('/corporate/post-job')} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Post Your First Requirement
                  </Button>
                </div>
              ) : (
                jobs.map(job => (
                  <JobCard 
                    key={job.id} 
                    job={job} 
                    onView={() => navigate(`/job/${job.id}`)}
                    onUpdateAppStatus={handleUpdateApplicationStatus}
                    token={token}
                  />
                ))
              )}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="space-y-4">
              {/* Booking Time Filter */}
              <div className="flex gap-2 mb-2">
                <Button
                  size="sm"
                  variant={bookingFilter === 'all' ? 'default' : 'outline'}
                  onClick={() => setBookingFilter('all')}
                  data-testid="filter-all-bookings"
                >
                  All ({bookings.length})
                </Button>
                <Button
                  size="sm"
                  variant={bookingFilter === 'pending' ? 'default' : 'outline'}
                  onClick={() => setBookingFilter('pending')}
                  className={bookingFilter === 'pending' ? '' : 'text-amber-600 border-amber-200'}
                  data-testid="filter-pending-bookings"
                >
                  Pending ({bookings.filter(b => b.status === 'pending_approval').length})
                </Button>
                <Button
                  size="sm"
                  variant={bookingFilter === 'upcoming' ? 'default' : 'outline'}
                  onClick={() => setBookingFilter('upcoming')}
                  data-testid="filter-upcoming-bookings"
                >
                  Upcoming ({bookings.filter(b => new Date(b.start_datetime) > new Date() && b.status !== 'rejected').length})
                </Button>
                <Button
                  size="sm"
                  variant={bookingFilter === 'past' ? 'default' : 'outline'}
                  onClick={() => setBookingFilter('past')}
                  data-testid="filter-past-bookings"
                >
                  Past ({bookings.filter(b => new Date(b.end_datetime) < new Date() && b.status !== 'rejected').length})
                </Button>
              </div>

              {(() => {
                const filtered = bookings.filter(b => {
                  if (bookingFilter === 'pending') return b.status === 'pending_approval';
                  if (bookingFilter === 'upcoming') return new Date(b.start_datetime) > new Date() && b.status !== 'rejected';
                  if (bookingFilter === 'past') return new Date(b.end_datetime) < new Date() && b.status !== 'rejected';
                  return true;
                });

                return filtered.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No bookings yet</h3>
                  <p className="text-slate-500">Your confirmed trainer bookings will appear here</p>
                </div>
              ) : (
                filtered.map(booking => {
                  const tStatus = booking.training_status || 'confirmed';
                  const isPending = booking.status === 'pending_approval';
                  const isRejected = booking.status === 'rejected';
                  return (
                    <div key={booking.id} className={`bg-white rounded-xl border p-6 ${isPending ? 'border-amber-200 bg-amber-50/30' : isRejected ? 'border-red-200 bg-red-50/30' : 'border-slate-200'}`} data-testid={`corp-booking-${booking.id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-lg text-slate-900 mb-1">{booking.job_title}</h4>
                          <p className="text-slate-500">Trainer: {booking.trainer_name}</p>
                        </div>
                        <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                          isPending ? 'bg-amber-100 text-amber-700' :
                          isRejected ? 'bg-red-100 text-red-700' :
                          tStatus === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                          tStatus === 'in_progress' ? 'bg-blue-50 text-blue-700' :
                          tStatus === 'feedback_pending' ? 'bg-purple-50 text-purple-700' :
                          'bg-amber-50 text-amber-700'
                        }`} data-testid={`corp-training-status-${booking.id}`}>
                          {isPending ? 'Awaiting Trainer Approval' :
                           isRejected ? 'Declined by Trainer' :
                           tStatus === 'in_progress' ? 'In Progress' :
                           tStatus === 'feedback_pending' ? 'Feedback Pending' :
                           tStatus === 'completed' ? 'Training Complete' : 'Confirmed'}
                        </span>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-slate-500 mb-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(booking.start_datetime).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(booking.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - 
                          {new Date(booking.end_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          ₹{booking.amount?.toLocaleString()}
                        </span>
                        {booking.feedback_count > 0 && (
                          <span className="flex items-center gap-1 text-purple-600">
                            <MessageSquare className="w-4 h-4" />
                            {booking.feedback_count} feedback(s)
                          </span>
                        )}
                      </div>

                      {/* Completion Badge */}
                      {tStatus === 'completed' && (
                        <div className="mb-4 p-3 bg-emerald-50 rounded-lg border border-emerald-100 flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                          <span className="text-sm font-medium text-emerald-800">Training Complete — {booking.feedback_count} participant feedback(s) received</span>
                        </div>
                      )}

                      <div className="flex items-center justify-end border-t border-slate-100 pt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setContractBooking(booking)}
                          data-testid={`view-contract-${booking.id}`}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          View Contract
                        </Button>
                      </div>
                    </div>
                  );
                })
              );
              })()}
            </div>
          </TabsContent>

          {/* Advertisements Tab */}
          <TabsContent value="ads">
            <div className="space-y-6">
              {/* Create Ad Button */}
              <div className="flex items-center justify-between">
                <h3 className="font-outfit font-semibold text-lg text-slate-900">Your Advertisements</h3>
                <Button
                  onClick={() => setShowCreateAd(!showCreateAd)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="create-ad-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Ad
                </Button>
              </div>

              {/* Create Ad Form */}
              {showCreateAd && (
                <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="create-ad-form">
                  <h4 className="font-semibold text-slate-900">New Advertisement</h4>
                  <p className="text-sm text-slate-500">Your ad will appear on search results after admin approval.</p>
                  <div>
                    <Label>Headline *</Label>
                    <Input
                      value={adForm.headline}
                      onChange={(e) => setAdForm(prev => ({ ...prev, headline: e.target.value }))}
                      placeholder="e.g., Expert Corporate Training by TCS"
                      className="mt-1"
                      data-testid="ad-headline-input"
                    />
                  </div>
                  <div>
                    <Label>Description *</Label>
                    <Textarea
                      value={adForm.description}
                      onChange={(e) => setAdForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of your training services..."
                      className="mt-1"
                      rows={3}
                      data-testid="ad-description-input"
                    />
                  </div>
                  <div>
                    <Label>Website / Link (optional)</Label>
                    <Input
                      value={adForm.link_url}
                      onChange={(e) => setAdForm(prev => ({ ...prev, link_url: e.target.value }))}
                      placeholder="https://yourcompany.com"
                      className="mt-1"
                      data-testid="ad-link-input"
                    />
                  </div>
                  <div>
                    <Label>Training Types (comma-separated)</Label>
                    <Input
                      value={adForm.training_types}
                      onChange={(e) => setAdForm(prev => ({ ...prev, training_types: e.target.value }))}
                      placeholder="e.g., Leadership, IT Training, Sales"
                      className="mt-1"
                      data-testid="ad-types-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={async () => {
                        if (!adForm.headline || !adForm.description) {
                          toast.error('Headline and description are required');
                          return;
                        }
                        try {
                          await axios.post(`${API}/ads`, {
                            headline: adForm.headline,
                            description: adForm.description,
                            link_url: adForm.link_url,
                            training_types: adForm.training_types ? adForm.training_types.split(',').map(t => t.trim()) : []
                          }, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success('Ad submitted for approval!');
                          setAdForm({ headline: '', description: '', link_url: '', training_types: '' });
                          setShowCreateAd(false);
                          fetchData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to create ad');
                        }
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="submit-ad-btn"
                    >
                      Submit for Approval
                    </Button>
                    <Button variant="outline" onClick={() => setShowCreateAd(false)}>Cancel</Button>
                  </div>
                </div>
              )}

              {/* Ads List */}
              {myAds.length === 0 && !showCreateAd ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-outfit text-lg font-semibold text-slate-900 mb-2">No Advertisements Yet</h4>
                  <p className="text-slate-500 mb-4">Create an ad to promote your training services on the search results page.</p>
                  <Button onClick={() => setShowCreateAd(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Create Your First Ad
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {myAds.map(ad => (
                    <div key={ad.id} className="bg-white rounded-xl border border-slate-200 p-5 flex items-start justify-between" data-testid={`ad-item-${ad.id}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-semibold text-slate-900">{ad.headline}</h4>
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                            ad.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                            ad.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                            'bg-red-50 text-red-700'
                          }`}>
                            {ad.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 line-clamp-2">{ad.description}</p>
                        {ad.training_types?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {ad.training_types.map(t => (
                              <span key={t} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">{t}</span>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-slate-400 mt-2">{new Date(ad.created_at).toLocaleDateString()}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={async () => {
                          try {
                            await axios.delete(`${API}/ads/${ad.id}`, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success('Ad deleted');
                            fetchData();
                          } catch (err) { toast.error('Failed to delete ad'); }
                        }}
                        data-testid={`delete-ad-${ad.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Interviews Tab */}
          <TabsContent value="interviews">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="font-outfit text-xl font-semibold text-slate-900">Interview Schedule</h2>
                <Button
                  onClick={() => setShowScheduleInterview(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="schedule-interview-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Interview
                </Button>
              </div>

              {interviews.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Video className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No interviews scheduled</h3>
                  <p className="text-slate-500 mb-6">Schedule Google Meet interviews with trainers for your requirements</p>
                  <Button onClick={() => setShowScheduleInterview(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Schedule First Interview
                  </Button>
                </div>
              ) : (
                interviews.map(interview => (
                  <div key={interview.id} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`interview-${interview.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-1">{interview.job_title}</h3>
                        <p className="text-sm text-slate-500 mb-3">Trainer: {interview.trainer_name}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4 text-blue-500" />
                            {new Date(interview.proposed_datetime).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4 text-blue-500" />
                            {new Date(interview.proposed_datetime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} ({interview.duration_minutes} min)
                          </span>
                        </div>
                        {interview.notes && <p className="text-sm text-slate-500 mt-2">{interview.notes}</p>}
                        {interview.meet_link && (
                          <a
                            href={interview.meet_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-blue-600 hover:text-blue-800"
                            data-testid="join-meet-link"
                          >
                            <Video className="w-4 h-4" />
                            Join Google Meet
                          </a>
                        )}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        interview.status === 'accepted' ? 'bg-emerald-100 text-emerald-800' :
                        interview.status === 'declined' ? 'bg-red-100 text-red-800' :
                        interview.status === 'cancelled' ? 'bg-slate-100 text-slate-600' :
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {interview.status.charAt(0).toUpperCase() + interview.status.slice(1)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Team Management Tab */}
          <TabsContent value="team">
            <div className="space-y-6" data-testid="team-section">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-outfit font-semibold text-xl text-slate-900">Team Management</h3>
                  <p className="text-sm text-slate-500 mt-1">Manage who can access your company account and their permissions</p>
                </div>
                <Button
                  onClick={() => setShowInviteTeam(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="invite-team-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite Member
                </Button>
              </div>

              {/* Invite Form */}
              {showInviteTeam && (
                <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="invite-team-form">
                  <h4 className="font-semibold text-slate-900">Invite Team Member</h4>
                  <p className="text-sm text-slate-500">If the user already has an account, they&apos;ll be linked automatically.</p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Name *</Label>
                      <Input
                        value={teamForm.name}
                        onChange={(e) => setTeamForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Full name"
                        className="mt-1"
                        data-testid="team-name-input"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={teamForm.email}
                        onChange={(e) => setTeamForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="member@company.com"
                        className="mt-1"
                        data-testid="team-email-input"
                      />
                    </div>
                    <div>
                      <Label>Role *</Label>
                      <select
                        value={teamForm.role}
                        onChange={(e) => setTeamForm(prev => ({ ...prev, role: e.target.value }))}
                        className="w-full h-10 px-3 mt-1 rounded-lg border border-slate-200"
                        data-testid="team-role-select"
                      >
                        <option value="viewer">Viewer — can browse</option>
                        <option value="manager">Manager — can post jobs &amp; manage bookings</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={async () => {
                        if (!teamForm.name || !teamForm.email) { toast.error('Name and email are required'); return; }
                        try {
                          await axios.post(`${API}/teams/invite`, teamForm, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success('Team member invited!');
                          setTeamForm({ name: '', email: '', role: 'viewer' });
                          setShowInviteTeam(false);
                          fetchData();
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to invite'); }
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="confirm-invite-team-btn"
                    >
                      Send Invitation
                    </Button>
                    <Button variant="outline" onClick={() => setShowInviteTeam(false)}>Cancel</Button>
                  </div>
                </div>
              )}

              {/* Team Members List */}
              {teamMembers.length === 0 && !showInviteTeam ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-outfit text-lg font-semibold text-slate-900 mb-2">No Team Members</h4>
                  <p className="text-slate-500 mb-6">Invite colleagues to collaborate on hiring trainers</p>
                  <Button onClick={() => setShowInviteTeam(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <UserPlus className="w-4 h-4 mr-2" /> Invite First Member
                  </Button>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="divide-y divide-slate-100">
                    {teamMembers.map(member => (
                      <div key={member.id} className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors" data-testid={`team-member-${member.id}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm ${
                            member.role === 'owner' ? 'bg-amber-500' :
                            member.role === 'manager' ? 'bg-blue-500' :
                            'bg-slate-400'
                          }`}>
                            {member.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <h5 className="font-medium text-slate-900">{member.name}</h5>
                            <p className="text-xs text-slate-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            member.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                          }`}>
                            {member.status}
                          </span>
                          {member.role === 'owner' ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-800 text-xs font-semibold rounded-full border border-amber-200">
                              <Shield className="w-3 h-3" /> Owner
                            </span>
                          ) : (
                            <select
                              value={member.role}
                              onChange={async (e) => {
                                try {
                                  await axios.put(`${API}/teams/${member.id}/role?role=${e.target.value}`, {}, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Role updated');
                                  fetchData();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update role'); }
                              }}
                              className="h-8 px-2 text-xs rounded-lg border border-slate-200"
                              data-testid={`role-select-${member.id}`}
                            >
                              <option value="manager">Manager</option>
                              <option value="viewer">Viewer</option>
                            </select>
                          )}
                          {member.role !== 'owner' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={async () => {
                                try {
                                  await axios.delete(`${API}/teams/${member.id}`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Member removed');
                                  fetchData();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
                              }}
                              data-testid={`remove-member-${member.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">
                    {teamMembers.length} / 10 members. Owners can invite, change roles, and remove members.
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6" data-testid="corp-profile-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-outfit font-semibold text-xl text-slate-900">Company Profile</h3>
                <Button
                  size="sm"
                  variant={editingCorpProfile ? "default" : "outline"}
                  onClick={() => setEditingCorpProfile(!editingCorpProfile)}
                  data-testid="edit-corp-profile-btn"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  {editingCorpProfile ? 'Cancel' : 'Edit Profile'}
                </Button>
              </div>

              {editingCorpProfile ? (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name</Label>
                      <Input
                        value={corpProfileForm.company_name}
                        onChange={(e) => setCorpProfileForm(f => ({ ...f, company_name: e.target.value }))}
                        className="mt-1"
                        data-testid="corp-name-input"
                      />
                    </div>
                    <div>
                      <Label>Industry</Label>
                      <Input
                        value={corpProfileForm.industry}
                        onChange={(e) => setCorpProfileForm(f => ({ ...f, industry: e.target.value }))}
                        className="mt-1"
                        placeholder="e.g. IT, Finance, Healthcare"
                        data-testid="corp-industry-input"
                      />
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Input
                        value={corpProfileForm.location}
                        onChange={(e) => setCorpProfileForm(f => ({ ...f, location: e.target.value }))}
                        className="mt-1"
                        placeholder="e.g. Mumbai, India"
                        data-testid="corp-location-input"
                      />
                    </div>
                    <div>
                      <Label>Company Size</Label>
                      <select
                        value={corpProfileForm.company_size}
                        onChange={(e) => setCorpProfileForm(f => ({ ...f, company_size: e.target.value }))}
                        className="w-full h-10 px-3 mt-1 rounded-lg border border-slate-200 text-sm"
                        data-testid="corp-size-select"
                      >
                        <option value="">Select size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="500+">500+ employees</option>
                      </select>
                    </div>
                    <div>
                      <Label>Website</Label>
                      <Input
                        value={corpProfileForm.website}
                        onChange={(e) => setCorpProfileForm(f => ({ ...f, website: e.target.value }))}
                        className="mt-1"
                        placeholder="https://company.com"
                        data-testid="corp-website-input"
                      />
                    </div>
                    <div>
                      <Label>Contact Phone</Label>
                      <Input
                        value={corpProfileForm.contact_phone}
                        onChange={(e) => setCorpProfileForm(f => ({ ...f, contact_phone: e.target.value }))}
                        className="mt-1"
                        placeholder="+91 9876543210"
                        data-testid="corp-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>About</Label>
                    <Textarea
                      value={corpProfileForm.about}
                      onChange={(e) => setCorpProfileForm(f => ({ ...f, about: e.target.value }))}
                      className="mt-1"
                      rows={3}
                      placeholder="Brief description of your company..."
                      data-testid="corp-about-input"
                    />
                  </div>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/corporates/${profile.id}`, corpProfileForm, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success('Profile updated!');
                        setEditingCorpProfile(false);
                        fetchData();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
                    }}
                    data-testid="save-corp-profile-btn"
                  >
                    Save Changes
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Company Name</p>
                      <p className="font-medium text-slate-900 mt-1">{profile?.company_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Industry</p>
                      <p className="font-medium text-slate-900 mt-1">{profile?.industry || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Location</p>
                      <p className="font-medium text-slate-900 mt-1">{profile?.location || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Company Size</p>
                      <p className="font-medium text-slate-900 mt-1">{profile?.company_size || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Website</p>
                      <p className="font-medium text-slate-900 mt-1">{profile?.website ? <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{profile.website}</a> : '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Contact Phone</p>
                      <p className="font-medium text-slate-900 mt-1">{profile?.contact_phone || '—'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">About</p>
                      <p className="font-medium text-slate-900 mt-1">{profile?.about || '—'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Training Contract Modal */}
      {contractBooking && (
        <TrainingContract 
          booking={contractBooking} 
          onClose={() => setContractBooking(null)} 
        />
      )}

      {/* Booking Dialog */}
      <Dialog open={bookingDialog} onOpenChange={setBookingDialog}>
        <DialogContent className="max-w-md border-2 border-blue-400 shadow-[0_0_30px_rgba(59,130,246,0.3)] animate-in fade-in-0 zoom-in-95">
          <DialogHeader>
            <DialogTitle className="text-lg font-outfit font-semibold">Book Trainer</DialogTitle>
          </DialogHeader>
          {selectedTrainer && (
            <div className="space-y-6">
              {/* Trainer Info */}
              <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                  {selectedTrainer.name?.charAt(0)}
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">{selectedTrainer.name}</h4>
                  <p className="text-sm text-slate-500">₹{selectedTrainer.hourly_rate}/hr</p>
                </div>
              </div>

              {/* Select Job */}
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">Select Job *</label>
                <select
                  value={selectedJob?.id || ''}
                  onChange={(e) => setSelectedJob(jobs.find(j => j.id === e.target.value))}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                  data-testid="booking-job-select"
                >
                  <option value="">Select a job</option>
                  {jobs.filter(j => j.status === 'open').map(job => (
                    <option key={job.id} value={job.id}>{job.title}</option>
                  ))}
                </select>
              </div>

              {/* Date & Time with Confirm Step */}
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">Start Date & Time *</label>
                <Input
                  type="datetime-local"
                  value={bookingForm.start_datetime}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, start_datetime: e.target.value }))}
                  data-testid="booking-start-datetime"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">End Date & Time *</label>
                <Input
                  type="datetime-local"
                  value={bookingForm.end_datetime}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, end_datetime: e.target.value }))}
                  data-testid="booking-end-datetime"
                />
              </div>

              {bookingForm.start_datetime && bookingForm.end_datetime && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                  Selected: {new Date(bookingForm.start_datetime).toLocaleDateString()} {new Date(bookingForm.start_datetime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})} - {new Date(bookingForm.end_datetime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">Amount (₹)</label>
                <Input
                  type="number"
                  value={bookingForm.amount}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                  placeholder={`Suggested: ₹${selectedTrainer.hourly_rate * 8}`}
                  data-testid="booking-amount-input"
                />
              </div>

              <Button
                onClick={handleCreateBooking}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 font-medium text-base"
                disabled={!selectedJob || !bookingForm.start_datetime || !bookingForm.end_datetime}
                data-testid="confirm-booking-btn"
              >
                Confirm Booking Request
              </Button>
              <p className="text-xs text-slate-400 text-center">Trainer will review and accept/decline this request</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Interview Dialog */}
      <Dialog open={showScheduleInterview} onOpenChange={setShowScheduleInterview}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Schedule Google Meet Interview</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium text-slate-700">Select Requirement *</Label>
              <select
                value={interviewForm.job_id}
                onChange={(e) => setInterviewForm(prev => ({ ...prev, job_id: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 mt-1"
                data-testid="interview-job-select"
              >
                <option value="">Choose a requirement</option>
                {jobs.filter(j => j.status === 'open' || j.status === 'filled').map(job => (
                  <option key={job.id} value={job.id}>{job.title}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700">Trainer ID *</Label>
              <Input
                value={interviewForm.trainer_id}
                onChange={(e) => setInterviewForm(prev => ({ ...prev, trainer_id: e.target.value }))}
                placeholder="Paste trainer ID from their profile"
                className="mt-1"
                data-testid="interview-trainer-input"
              />
              <p className="text-xs text-slate-400 mt-1">Find trainer IDs from the Search page or AI Recommendations</p>
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700">Date & Time *</Label>
              <Input
                type="datetime-local"
                value={interviewForm.proposed_datetime}
                onChange={(e) => setInterviewForm(prev => ({ ...prev, proposed_datetime: e.target.value }))}
                className="mt-1"
                data-testid="interview-datetime-input"
              />
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700">Duration (minutes)</Label>
              <select
                value={interviewForm.duration_minutes}
                onChange={(e) => setInterviewForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 mt-1"
                data-testid="interview-duration-select"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes</option>
              </select>
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700">Google Meet Link</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={interviewForm.meet_link}
                  onChange={(e) => setInterviewForm(prev => ({ ...prev, meet_link: e.target.value }))}
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                  data-testid="interview-meet-link-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open('https://meet.google.com/new', '_blank')}
                  className="shrink-0"
                  data-testid="create-meet-btn"
                >
                  <Video className="w-4 h-4 mr-1" /> Create
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Click &ldquo;Create&rdquo; to generate a new Google Meet link, then paste it here</p>
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700">Notes (optional)</Label>
              <Textarea
                value={interviewForm.notes}
                onChange={(e) => setInterviewForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Topics to discuss, preparation needed, etc."
                className="mt-1"
                rows={2}
                data-testid="interview-notes-input"
              />
            </div>

            <Button
              onClick={handleScheduleInterview}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="confirm-schedule-interview-btn"
            >
              <Video className="w-4 h-4 mr-2" />
              Schedule Interview
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Job Card Component with Applications and AI Recommendations
const JobCard = ({ job, onView, onUpdateAppStatus, token }) => {
  const [applications, setApplications] = useState([]);
  const [showApps, setShowApps] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [recLoading, setRecLoading] = useState(false);
  const [invitedTrainers, setInvitedTrainers] = useState(new Set());
  const [inviting, setInviting] = useState(null);
  const [bulkInviting, setBulkInviting] = useState(false);
  const [selectedForBulk, setSelectedForBulk] = useState(new Set());
  const navigate = useNavigate();

  const fetchApplications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/applications/job/${job.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    }
  }, [job.id, token]);

  const fetchRecommendations = async () => {
    if (recommendations.length > 0) {
      setShowRecommendations(!showRecommendations);
      return;
    }
    setRecLoading(true);
    setShowRecommendations(true);
    try {
      const response = await axios.get(`${API}/jobs/${job.id}/ai-recommendations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      toast.error('Failed to get AI recommendations');
      console.error('AI recommendations error:', error);
    } finally {
      setRecLoading(false);
    }
  };

  const handleInvite = async (trainerId, trainerName) => {
    setInviting(trainerId);
    try {
      await axios.post(`${API}/jobs/${job.id}/invite-trainer?trainer_id=${trainerId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invitation sent to ${trainerName}!`);
      setInvitedTrainers(prev => new Set([...prev, trainerId]));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(null);
    }
  };

  const handleBulkInvite = async (trainerIds) => {
    setBulkInviting(true);
    try {
      const res = await axios.post(`${API}/jobs/${job.id}/bulk-invite`, trainerIds, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success(`Sent ${res.data.sent} invitations! (${res.data.skipped} already invited)`);
      setInvitedTrainers(prev => new Set([...prev, ...trainerIds]));
      setSelectedForBulk(new Set());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk invite failed');
    } finally {
      setBulkInviting(false);
    }
  };

  useEffect(() => {
    if (showApps) {
      fetchApplications();
    }
  }, [showApps, fetchApplications]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    if (score >= 60) return 'text-blue-700 bg-blue-50 border-blue-200';
    return 'text-amber-700 bg-amber-50 border-amber-200';
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`job-card-${job.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h4 className="font-semibold text-lg text-slate-900">{job.title}</h4>
            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
              job.status === 'open'
                ? 'bg-emerald-50 text-emerald-700'
                : job.status === 'filled'
                ? 'bg-blue-50 text-blue-700'
                : 'bg-slate-100 text-slate-500'
            }`}>
              {job.status}
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            {job.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {job.location}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {new Date(job.start_date).toLocaleDateString()}
            </span>
            <span className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              ₹{job.budget?.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {job.applications_count} applications
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onView}>
            <Eye className="w-4 h-4 mr-1" />
            View
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowApps(!showApps)}
            data-testid={`toggle-apps-${job.id}`}
          >
            Applications ({job.applications_count})
          </Button>
        </div>
      </div>

      {/* Skills */}
      {job.skills_required?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {job.skills_required.slice(0, 5).map(skill => (
            <span key={skill} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">
              {skill}
            </span>
          ))}
        </div>
      )}

      {/* AI Recommendations Button */}
      {job.status === 'open' && (
        <div className="mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRecommendations}
            className="border-purple-200 text-purple-700 hover:bg-purple-50"
            data-testid={`ai-recommend-${job.id}`}
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            {recLoading ? 'Analyzing Trainers...' : showRecommendations ? 'Hide AI Recommendations' : 'AI Recommended Trainers'}
            {!recLoading && (showRecommendations ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
          </Button>
        </div>
      )}

      {/* AI Recommendations Section */}
      {showRecommendations && (
        <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100" data-testid={`recommendations-${job.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <h5 className="font-semibold text-sm text-purple-900">AI Recommended Trainers</h5>
            {recommendations.length > 0 && (
              <span className="text-xs text-purple-500">Top {recommendations.length} matches</span>
            )}
            {recommendations.length > 1 && (
              <div className="ml-auto flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7 border-purple-200 text-purple-700"
                  onClick={() => {
                    const eligible = recommendations.filter(r => !r.already_applied && !invitedTrainers.has(r.trainer_id)).map(r => r.trainer_id);
                    setSelectedForBulk(prev => prev.size === eligible.length ? new Set() : new Set(eligible));
                  }}
                  data-testid={`select-all-trainers-${job.id}`}
                >
                  {selectedForBulk.size > 0 ? 'Deselect All' : 'Select All'}
                </Button>
                {selectedForBulk.size > 0 && (
                  <Button
                    size="sm"
                    onClick={() => handleBulkInvite([...selectedForBulk])}
                    disabled={bulkInviting}
                    className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-7"
                    data-testid={`bulk-invite-btn-${job.id}`}
                  >
                    {bulkInviting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                    Invite Selected ({selectedForBulk.size})
                  </Button>
                )}
              </div>
            )}
          </div>
          
          {recLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 text-purple-500 animate-spin mr-2" />
              <span className="text-sm text-purple-600">Analyzing trainers with AI...</span>
            </div>
          ) : recommendations.length === 0 ? (
            <p className="text-sm text-purple-600 py-3">No matching trainers found. Try updating the job requirements.</p>
          ) : (
            <div className="space-y-3">
              {recommendations.map((rec, idx) => (
                <div key={rec.trainer_id} className="bg-white rounded-lg p-3 border border-purple-100 hover:border-purple-200 transition-all" data-testid={`rec-trainer-${rec.trainer_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      {!rec.already_applied && !invitedTrainers.has(rec.trainer_id) && (
                        <input
                          type="checkbox"
                          checked={selectedForBulk.has(rec.trainer_id)}
                          onChange={() => {
                            setSelectedForBulk(prev => {
                              const next = new Set(prev);
                              next.has(rec.trainer_id) ? next.delete(rec.trainer_id) : next.add(rec.trainer_id);
                              return next;
                            });
                          }}
                          className="w-4 h-4 rounded border-slate-300 text-purple-600 mt-1 shrink-0"
                          data-testid={`bulk-check-${rec.trainer_id}`}
                        />
                      )}
                      <div className="relative">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                          {rec.name?.charAt(0)}
                        </div>
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-sm border">
                          <span className="text-[10px] font-bold text-purple-600">#{idx + 1}</span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span 
                            className="font-semibold text-sm text-slate-900 hover:text-blue-600 cursor-pointer"
                            onClick={() => navigate(`/trainer/${rec.trainer_id}`)}
                          >
                            {rec.name}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-bold rounded-full border ${getScoreColor(rec.match_score)}`}>
                            {rec.match_score}% match
                          </span>
                          {rec.already_applied && (
                            <span className="px-2 py-0.5 text-xs bg-green-50 text-green-700 rounded-full border border-green-200">
                              Already Applied
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                          {rec.location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{rec.location}</span>}
                          {rec.experience_years > 0 && <span>{rec.experience_years} yrs exp</span>}
                          {rec.rating > 0 && <span className="flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-400 fill-amber-400" />{rec.rating}</span>}
                          {rec.hourly_rate > 0 && <span>₹{rec.hourly_rate}/hr</span>}
                        </div>
                        {rec.highlight && (
                          <p className="text-xs text-purple-600 mt-1 italic">{rec.highlight}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-3">
                      {!rec.already_applied && (
                        <Button
                          size="sm"
                          onClick={() => handleInvite(rec.trainer_id, rec.name)}
                          disabled={invitedTrainers.has(rec.trainer_id) || inviting === rec.trainer_id}
                          className={invitedTrainers.has(rec.trainer_id) 
                            ? 'bg-green-600 text-white text-xs h-8' 
                            : 'bg-purple-600 hover:bg-purple-700 text-white text-xs h-8'}
                          data-testid={`invite-trainer-${rec.trainer_id}`}
                        >
                          {inviting === rec.trainer_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : invitedTrainers.has(rec.trainer_id) ? (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Invited</>
                          ) : (
                            <><Send className="w-3 h-3 mr-1" /> Invite</>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Match Reasons */}
                  {rec.reasons?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {rec.reasons.map((reason, i) => (
                        <span key={i} className="px-2 py-0.5 text-[10px] bg-purple-50 text-purple-600 rounded-full border border-purple-100">
                          {reason}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Applications List */}
      {showApps && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3" data-testid={`applications-list-${job.id}`}>
          {applications.length === 0 ? (
            <p className="text-slate-500 text-sm">No applications yet</p>
          ) : (
            applications.map(app => (
              <div key={app.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div 
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={() => navigate(`/trainer/${app.trainer_id}`)}
                >
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                    {app.trainer_name?.charAt(0)}
                  </div>
                  <div>
                    <h5 className="font-medium text-slate-900">{app.trainer_name}</h5>
                    <p className="text-xs text-slate-500">
                      Applied {new Date(app.applied_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {app.status === 'pending' ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => onUpdateAppStatus(app.id, 'rejected')}
                        data-testid={`reject-app-${app.id}`}
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => onUpdateAppStatus(app.id, 'accepted')}
                        data-testid={`accept-app-${app.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Accept
                      </Button>
                    </>
                  ) : (
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      app.status === 'accepted'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-red-50 text-red-700'
                    }`}>
                      {app.status}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
